/**
 * Turn detection for the figure-8 test on normalised series (see sensorFormat.js).
 * Primary signal: gyroZ (yaw rate, deg/s). Fallback: lateral accel (accelX, g) when gyro is absent.
 * Works with any number of samples.
 */

const ENV = {};

const TURN_RATE_DPS = Number(ENV.TURN_RATE_DPS || 5); // smoothed |gyroZ| above this = turning
const MIN_TURN_ANGLE_DEG = Number(ENV.MIN_TURN_ANGLE_DEG || 20); // ignore tiny wiggles
const LATERAL_G = Number(ENV.TURN_LATERAL_G || 0.08); // fallback threshold on accelX deviation
// Right-hand rule with Z up: positive yaw rate = counter-clockwise = LEFT. Set to -1 if mounted inverted.
const GYRO_LEFT_SIGN = Number(ENV.GYRO_LEFT_SIGN || 1);
const DEFAULT_SEQUENCE = ["RIGHT", "LEFT", "RIGHT", "LEFT"];

const mean = (xs) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);
const round = (v, d = 2) => Math.round(v * 10 ** d) / 10 ** d;

function extractTurns(series, session = {}, options = {}) {
  const n = series.length;
  if (n < 3) return { method: "none", turns: [] };
  const dt = session.dtSec || 0.5;
  const useGyro = session.hasGyro !== false && series.some((p) => Math.abs(p.gz || 0) > 0);
  const threshold = useGyro ? options.rateThreshold || TURN_RATE_DPS : options.lateralThreshold || LATERAL_G;
  const raw = series.map((p) => (useGyro ? p.gz || 0 : p.ax || 0));
  const smooth = raw.map((_, i) => mean(raw.slice(Math.max(0, i - 1), i + 2)));

  // 1) segments of constant sign above the threshold
  const segs = [];
  let cur = null;
  smooth.forEach((v, i) => {
    const s = v > threshold ? 1 : v < -threshold ? -1 : 0;
    if (cur && s === cur.sign) cur.end = i;
    else {
      if (cur) segs.push(cur);
      cur = s ? { sign: s, start: i, end: i } : null;
    }
  });
  if (cur) segs.push(cur);

  // 2) merge same-direction segments separated by a single quiet sample
  const merged = [];
  segs.forEach((s) => {
    const last = merged[merged.length - 1];
    if (last && last.sign === s.sign && s.start - last.end <= 2) last.end = s.end;
    else merged.push({ ...s });
  });

  // 3) measure and keep real turns
  const turns = [];
  merged.forEach((s) => {
    const seg = raw.slice(s.start, s.end + 1);
    const angle = useGyro ? seg.reduce((a, v) => a + v * dt, 0) : null;
    const sign = useGyro ? Math.sign(angle) || s.sign : s.sign;
    if (useGyro && Math.abs(angle) < MIN_TURN_ANGLE_DEG) return;
    if (!useGyro && s.end - s.start + 1 < 2) return;
    const leftSign = useGyro ? GYRO_LEFT_SIGN : -1; // fallback: positive accelX = RIGHT
    turns.push({
      order: turns.length + 1,
      direction: sign === leftSign ? "LEFT" : "RIGHT",
      startIndex: s.start,
      endIndex: s.end,
      startT: series[s.start].t,
      endT: series[s.end].t,
      durationMs: Math.round((s.end - s.start + 1) * dt * 1000),
      peakRate: round(Math.max(...seg.map(Math.abs)), 3), // deg/s (gyro) or g (fallback)
      avgRate: round(mean(seg.map(Math.abs)), 3),
      angleDeg: angle === null ? null : round(Math.abs(angle), 1),
    });
  });

  return { method: useGyro ? "gyroZ" : "accelX", threshold, turns };
}

/** Compare detected turns with the expected sequence and the reference limits. */
function evaluateTurns(detection, profile) {
  const turns = detection.turns || [];
  const expectedSequence = profile?.expectedSequence?.length ? profile.expectedSequence : DEFAULT_SEQUENCE;
  const failures = [];
  const warnings = [];

  const detectedSequence = turns.map((t) => t.direction);
  const sequenceOk =
    detectedSequence.length === expectedSequence.length &&
    detectedSequence.every((d, i) => d === expectedSequence[i]);

  if (turns.length !== expectedSequence.length) {
    failures.push(`Expected ${expectedSequence.length} turns, detected ${turns.length}`);
  }

  let limitIssues = 0;
  const checked = turns.map((t, i) => {
    const issues = [];
    if (expectedSequence[i] && t.direction !== expectedSequence[i]) {
      issues.push(`expected ${expectedSequence[i]}`);
      failures.push(`Turn ${t.order}: went ${t.direction}, expected ${expectedSequence[i]}`);
    }
    if (!expectedSequence[i]) issues.push("extra turn");
    const limit = profile?.turns?.byDirection?.[t.direction];
    if (limit) {
      if (limit.maxPeakRate && t.peakRate > limit.maxPeakRate) issues.push("turned too sharply");
      if (limit.maxDurationMs && t.durationMs > limit.maxDurationMs) issues.push("turn too slow");
      if (limit.minDurationMs && t.durationMs < limit.minDurationMs) issues.push("turn too quick");
    }
    const limitCount = issues.filter((m) => !m.startsWith("expected") && m !== "extra turn").length;
    limitIssues += limitCount;
    issues
      .filter((m) => !m.startsWith("expected"))
      .forEach((m) => warnings.push(`Turn ${t.order} (${t.direction}): ${m}`));
    return { ...t, expected: expectedSequence[i] || null, ok: issues.length === 0, issues };
  });

  const matched = checked.filter((t) => t.expected && t.direction === t.expected).length;
  const denom = Math.max(expectedSequence.length, turns.length) || 1;
  const score = Math.max(0, Math.round((matched / denom) * 100 - limitIssues * 10));

  return {
    method: detection.method,
    threshold: detection.threshold,
    calibrated: !!profile,
    expectedSequence,
    detectedSequence,
    expectedTurns: expectedSequence.length,
    turnCount: turns.length,
    matched,
    sequenceOk,
    avgDurationMs: turns.length ? Math.round(mean(turns.map((t) => t.durationMs))) : 0,
    turns: checked,
    score,
    failures,
    warnings,
    passed: sequenceOk,
  };
}

export { extractTurns, evaluateTurns, DEFAULT_SEQUENCE, TURN_RATE_DPS, MIN_TURN_ANGLE_DEG };
